import { graphql } from 'gatsby';
import {
  StyledLink,
  StyledDescription,
  StyledTitle,
  StyledImage,
  StyledIcon,
} from './ColumnSectionLink.styles';
import useLinkProps from '@/components/Link/hooks/useLinkProps';
import InfoTag from '@/components/InfoTag/InfoTag';

const DEFAULT_IMAGE_WIDTH = 176;
const DEFAULT_IMAGE_HEIGHT = 48;

interface ColumnSectionLinkProps extends Queries.HEADER_COLUMN_SECTION_LINKFragment {
  onLinkClick: () => void;
}

const ColumnSectionLink = ({ elements, onLinkClick }: ColumnSectionLinkProps) => {
  const {
    link: {
      value: { linkElements },
    },
    description,
    image,
    info_tag,
  } = elements;
  const { linkChildren, linkProps } = useLinkProps({
    action: linkElements.action.value,
    target: linkElements.target?.value,
    event: linkElements.event?.value,
  });
  const imageValue = image?.value && {
    ...image.value,
    description: image.value.description || undefined,
    width: image.value.width ?? DEFAULT_IMAGE_WIDTH,
    height: image.value.height ?? DEFAULT_IMAGE_HEIGHT,
  };
  const hasIcon = !!linkElements.icon?.value?.url;
  const hasInfoTag = !!info_tag?.value?.system.id;

  return (
    <StyledLink {...linkProps} role="menuitem" onClick={onLinkClick}>
      {imageValue && (
        <StyledImage
          image={{
            ...imageValue,
            image_id: imageValue.url,
          }}
          width={imageValue.width}
          height={imageValue.height}
          alt={imageValue.description}
          layout="fullWidth"
        />
      )}
      {linkChildren}
      <StyledTitle>
        {hasIcon && <StyledIcon {...linkElements.icon.value} />}
        {linkElements.text.value}
        {hasInfoTag && <InfoTag {...info_tag.value} size="small" />}
      </StyledTitle>
      {description?.value && <StyledDescription>{description.value}</StyledDescription>}
    </StyledLink>
  );
};

export const fragment = graphql`
  fragment HEADER_COLUMN_SECTION_LINK on kontent_item_header_column_section_link {
    system {
      id
    }
    elements {
      link {
        value {
          ...LINK
        }
      }
      description {
        value
      }
      image {
        value {
          description
          url
          width
          height
        }
      }
      info_tag {
        value {
          system {
            id
          }
          ...INFO_TAG
        }
      }
    }
  }
`;

export default ColumnSectionLink;
